import { inject, Injectable, NgZone } from '@angular/core';
import { Observable, timeout } from 'rxjs';
import { LoggerService } from '@pf/shared-services';
import { NativeCallbackRegistry } from './native-callback-registry';

export interface NativeInvokeOptions {
    /**
     * If set, the observable errors with a TimeoutError when the native side
     * does not respond within this many milliseconds.
     */
    timeoutMs?: number;
}

@Injectable({ providedIn: 'root' })
export class NativeBridgeService {
    private readonly ngZone = inject(NgZone);
    private readonly logger = inject(LoggerService);

    /**
     * Invokes an Android-native routine that returns its result asynchronously
     * by calling back into a callbackId registered on the shared window registry.
     *
     * Handles callbackId generation, NgZone re-entry, registry cleanup (including
     * on unsubscribe) and synchronous invocation failures. Callers map the raw
     * native result to their own domain type.
     *
     * @param routine short name, used for the callbackId prefix + logging
     * @param invoke  triggers the native call; receives the generated callbackId
     */
    invoke<TResult>(
        routine: string,
        invoke: (callbackId: string) => void,
        options?: NativeInvokeOptions
    ): Observable<TResult> {
        const source = new Observable<TResult>(subscriber => {
            const registry = this.getRegistry();
            const callbackId = this.createCallbackId(routine);

            registry[callbackId] = (result: unknown) => {
                this.ngZone.run(() => {
                    delete registry[callbackId];
                    subscriber.next(result as TResult);
                    subscriber.complete();
                });
            };

            try {
                invoke(callbackId);
            } catch (err) {
                delete registry[callbackId];
                this.logger.error(
                    `Native routine "${routine}" failed to invoke`,
                    err
                );
                subscriber.error(err);
            }

            // Clean up if the caller unsubscribes before the native side responds.
            return () => {
                delete registry[callbackId];
            };
        });

        return options?.timeoutMs
            ? source.pipe(timeout({ first: options.timeoutMs }))
            : source;
    }

    private getRegistry(): NativeCallbackRegistry {
        return (window.__ctNativeCallbacks ??= {});
    }

    private createCallbackId(routine: string): string {
        const rand = Math.random().toString(36).substring(2, 11);
        return `${routine}_${Date.now()}_${rand}`;
    }
}
